import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCookieConsent } from '../components/CookieConsent';
import { Button } from '../components/ui/button';
import { useLanguage } from '../i18n/LanguageContext';

const CookiePolicy = () => {
  const { language } = useLanguage();
  const cookieConsent = useCookieConsent();
  const lastUpdated = { en: "February 11, 2026", fi: "11. helmikuuta 2026" };

  const content = {
    en: {
      backHome: "Back to Home",
      title: "Cookie Policy",
      lastUpdatedLabel: "Last updated",
      manageHeading: "Manage Your Cookie Preferences",
      manageBody: "You can change your cookie settings at any time.",
      manageBtn: "Cookie Settings",
      tableCols: { name: "Cookie Name", purpose: "Purpose", duration: "Duration", provider: "Provider" },
      statusLabel: "Status",
      alwaysActive: "Always Active (Cannot be disabled)",
      requiresConsent: "Requires Consent",
      bottomCta: "Want to change your cookie preferences?",
      bottomCtaBtn: "Open Cookie Settings",
      sections: {
        what: {
          title: "1. What Are Cookies?",
          paragraphs: [
            "Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website. They are widely used to make websites work more efficiently, provide a better user experience, and give website owners information about how the site is used.",
            "This Cookie Policy explains how 2DS Finland Oy uses cookies and similar technologies on our website (2ds.fi) in compliance with the General Data Protection Regulation (GDPR) and the ePrivacy Directive."
          ]
        },
        types: {
          title: "2. Types of Cookies We Use",
          strict: {
            title: "2.1 Strictly Necessary Cookies",
            description: "These cookies are essential for the website to function properly. They enable basic functions like page navigation, secure access, and remembering your cookie preferences. The website cannot function properly without these cookies.",
            rows: [
              { name: "cookie_consent", purpose: "Stores your cookie preferences", duration: "12 months" },
              { name: "session_id", purpose: "Maintains your session", duration: "Session" },
              { name: "csrf_token", purpose: "Security - prevents cross-site request forgery", duration: "Session" }
            ]
          },
          analytics: {
            title: "2.2 Analytics & Performance Cookies",
            description: "These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website's functionality and content.",
            rows: [
              { name: "_ga", provider: "Google Analytics", purpose: "Distinguishes users", duration: "2 years" },
              { name: "_gid", provider: "Google Analytics", purpose: "Distinguishes users", duration: "24 hours" },
              { name: "posthog", provider: "PostHog", purpose: "Product analytics", duration: "1 year" }
            ]
          },
          functional: {
            title: "2.3 Functional Cookies",
            description: "These cookies enable enhanced functionality and personalization, such as remembering your language preferences or region. They may be set by us or by third-party providers.",
            rows: [
              { name: "language", purpose: "Remembers your language preference", duration: "12 months" },
              { name: "theme", purpose: "Stores display preferences", duration: "12 months" },
              { name: "timezone", purpose: "Stores your timezone for accurate time display", duration: "12 months" }
            ]
          },
          marketing: {
            title: "2.4 Marketing & Advertising Cookies",
            description: "These cookies are used to deliver advertisements more relevant to you and your interests. They are also used to limit the number of times you see an advertisement and help measure the effectiveness of advertising campaigns.",
            rows: [
              { name: "_fbp", provider: "Facebook", purpose: "Advertising tracking", duration: "3 months" },
              { name: "_gcl_au", provider: "Google Ads", purpose: "Conversion tracking", duration: "3 months" }
            ]
          }
        },
        consent: {
          title: "3. Your Consent",
          paragraphs: [
            "In accordance with GDPR and the ePrivacy Directive, we request your consent before placing non-essential cookies on your device. When you first visit our website, you will see a cookie consent banner that allows you to:"
          ],
          bullets: [
            "<strong>Accept All:</strong> Consent to all categories of cookies",
            "<strong>Necessary Only:</strong> Accept only strictly necessary cookies",
            "<strong>Customize:</strong> Choose which categories of cookies to accept"
          ],
          paragraphsAfter: ["Your consent is recorded and stored for 12 months. You can withdraw or modify your consent at any time by clicking the \"Cookie Settings\" button on this page or in the footer."]
        },
        control: {
          title: "4. How to Control Cookies",
          paragraphs: ["Besides using our cookie consent tool, you can also control cookies through your browser settings:"],
          links: [
            { label: "Google Chrome", url: "https://support.google.com/chrome/answer/95647" },
            { label: "Mozilla Firefox", url: "https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" },
            { label: "Apple Safari", url: "https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" },
            { label: "Microsoft Edge", url: "https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" }
          ],
          paragraphsAfter: ["<strong>Note:</strong> Blocking all cookies may affect the functionality of our website and other websites you visit."]
        },
        thirdParty: {
          title: "5. Third-Party Cookies",
          paragraphs: ["Some cookies are placed by third-party services that appear on our pages. We do not control these cookies. The third-party providers include:"],
          bullets: [
            "<strong>Google Analytics:</strong> <a href=\"https://policies.google.com/privacy\" class=\"text-accent-teal underline\" target=\"_blank\" rel=\"noopener noreferrer\">Privacy Policy</a>",
            "<strong>PostHog:</strong> <a href=\"https://posthog.com/privacy\" class=\"text-accent-teal underline\" target=\"_blank\" rel=\"noopener noreferrer\">Privacy Policy</a>"
          ]
        },
        updates: {
          title: "6. Updates to This Policy",
          paragraphs: ["We may update this Cookie Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. The \"Last updated\" date at the top indicates when changes were made. We encourage you to review this policy periodically."]
        },
        contact: {
          title: "7. Contact Us",
          paragraphs: ["If you have questions about our use of cookies or this Cookie Policy, please contact us:"],
          emailLabel: "Email",
          addressLabel: "Address"
        }
      }
    },
    fi: {
      backHome: "Takaisin etusivulle",
      title: "Evästekäytäntö",
      lastUpdatedLabel: "Päivitetty",
      manageHeading: "Hallinnoi evästeasetuksiasi",
      manageBody: "Voit muuttaa evästeasetuksiasi milloin tahansa.",
      manageBtn: "Evästeasetukset",
      tableCols: { name: "Evästeen nimi", purpose: "Käyttötarkoitus", duration: "Voimassaoloaika", provider: "Tarjoaja" },
      statusLabel: "Tila",
      alwaysActive: "Aina aktiivinen (ei voi poistaa käytöstä)",
      requiresConsent: "Vaatii suostumuksen",
      bottomCta: "Haluatko muuttaa evästeasetuksiasi?",
      bottomCtaBtn: "Avaa evästeasetukset",
      sections: {
        what: {
          title: "1. Mitä evästeet ovat?",
          paragraphs: [
            "Evästeet ovat pieniä tekstitiedostoja, jotka tallennetaan laitteellesi (tietokoneelle, tabletille tai mobiililaitteelle) verkkosivustolla vieraillessasi. Niitä käytetään yleisesti verkkosivustojen toiminnan tehostamiseen, käyttäjäkokemuksen parantamiseen ja sivustojen ylläpitäjille tarjotaan tietoa sivuston käytöstä.",
            "Tässä evästekäytännössä kuvataan, miten 2DS Finland Oy käyttää evästeitä ja vastaavia teknologioita verkkosivustollaan (2ds.fi) EU:n yleisen tietosuoja-asetuksen (GDPR) ja sähköisen viestinnän tietosuojadirektiivin mukaisesti."
          ]
        },
        types: {
          title: "2. Käyttämämme evästetyypit",
          strict: {
            title: "2.1 Välttämättömät evästeet",
            description: "Nämä evästeet ovat välttämättömiä sivuston toiminnalle. Ne mahdollistavat perustoiminnot, kuten sivunavigoinnin, turvallisen kirjautumisen ja evästeasetustesi muistamisen. Sivusto ei toimi kunnolla ilman näitä evästeitä.",
            rows: [
              { name: "cookie_consent", purpose: "Tallentaa evästeasetuksesi", duration: "12 kuukautta" },
              { name: "session_id", purpose: "Ylläpitää istuntoasi", duration: "Istunto" },
              { name: "csrf_token", purpose: "Turvallisuus – estää sivustojen välisen pyyntöväärennöksen", duration: "Istunto" }
            ]
          },
          analytics: {
            title: "2.2 Analytiikka- ja suorituskykyevästeet",
            description: "Nämä evästeet auttavat meitä ymmärtämään, miten kävijät käyttävät sivustoamme, keräämällä ja raportoimalla tietoja anonyymisti. Tämä auttaa meitä parantamaan sivuston toimintaa ja sisältöä.",
            rows: [
              { name: "_ga", provider: "Google Analytics", purpose: "Erottaa käyttäjät toisistaan", duration: "2 vuotta" },
              { name: "_gid", provider: "Google Analytics", purpose: "Erottaa käyttäjät toisistaan", duration: "24 tuntia" },
              { name: "posthog", provider: "PostHog", purpose: "Tuotteen analytiikka", duration: "1 vuosi" }
            ]
          },
          functional: {
            title: "2.3 Toiminnalliset evästeet",
            description: "Nämä evästeet mahdollistavat parannetut toiminnot ja personoinnin, kuten kielivalintasi tai alueesi muistamisen. Niitä voivat asettaa joko me tai kolmannet osapuolet.",
            rows: [
              { name: "language", purpose: "Muistaa kielivalintasi", duration: "12 kuukautta" },
              { name: "theme", purpose: "Tallentaa näyttöasetukset", duration: "12 kuukautta" },
              { name: "timezone", purpose: "Tallentaa aikavyöhykkeesi oikean ajan näyttämistä varten", duration: "12 kuukautta" }
            ]
          },
          marketing: {
            title: "2.4 Markkinointi- ja mainontaevästeet",
            description: "Näiden evästeiden avulla voimme näyttää sinulle kiinnostuksen kohteisiisi paremmin sopivia mainoksia. Niitä käytetään myös rajaamaan mainosten näyttökertoja ja mittaamaan mainoskampanjoiden tehokkuutta.",
            rows: [
              { name: "_fbp", provider: "Facebook", purpose: "Mainonnan seuranta", duration: "3 kuukautta" },
              { name: "_gcl_au", provider: "Google Ads", purpose: "Konversion seuranta", duration: "3 kuukautta" }
            ]
          }
        },
        consent: {
          title: "3. Suostumuksesi",
          paragraphs: [
            "GDPR:n ja sähköisen viestinnän tietosuojadirektiivin mukaisesti pyydämme suostumustasi ennen kuin asetamme laitteellesi ei-välttämättömiä evästeitä. Kun vierailet sivustollamme ensimmäistä kertaa, näet evästesuostumusbanneri, jonka avulla voit:"
          ],
          bullets: [
            "<strong>Hyväksy kaikki:</strong> Suostua kaikkiin evästekategorioihin",
            "<strong>Vain välttämättömät:</strong> Hyväksyä ainoastaan välttämättömät evästeet",
            "<strong>Mukauta:</strong> Valita, mitä evästekategorioita haluat hyväksyä"
          ],
          paragraphsAfter: ["Suostumuksesi tallennetaan ja säilytetään 12 kuukautta. Voit perua tai muuttaa suostumustasi milloin tahansa klikkaamalla \"Evästeasetukset\"-painiketta tällä sivulla tai sivuston alatunnisteessa."]
        },
        control: {
          title: "4. Evästeiden hallinta",
          paragraphs: ["Evästesuostumustyökalumme lisäksi voit hallita evästeitä myös selaimesi asetuksista:"],
          links: [
            { label: "Google Chrome", url: "https://support.google.com/chrome/answer/95647?hl=fi" },
            { label: "Mozilla Firefox", url: "https://support.mozilla.org/fi/kb/evasteet-tietoja-jotka-verkkosivut-tallentavat-omalle" },
            { label: "Apple Safari", url: "https://support.apple.com/fi-fi/guide/safari/sfri11471/mac" },
            { label: "Microsoft Edge", url: "https://support.microsoft.com/fi-fi/microsoft-edge/poista-ev%C3%A4steet-microsoft-edgess%C3%A4-63947406-40ac-c3b8-57b9-2a946a29ae09" }
          ],
          paragraphsAfter: ["<strong>Huom:</strong> Kaikkien evästeiden estäminen voi vaikuttaa sivustomme ja muiden vierailemiesi sivustojen toimintaan."]
        },
        thirdParty: {
          title: "5. Kolmannen osapuolen evästeet",
          paragraphs: ["Osan evästeistä asettavat kolmannen osapuolen palvelut, joita näkyy sivuillamme. Emme hallinnoi näitä evästeitä. Kolmannen osapuolen palveluntarjoajia ovat:"],
          bullets: [
            "<strong>Google Analytics:</strong> <a href=\"https://policies.google.com/privacy\" class=\"text-accent-teal underline\" target=\"_blank\" rel=\"noopener noreferrer\">Tietosuojakäytäntö</a>",
            "<strong>PostHog:</strong> <a href=\"https://posthog.com/privacy\" class=\"text-accent-teal underline\" target=\"_blank\" rel=\"noopener noreferrer\">Tietosuojakäytäntö</a>"
          ]
        },
        updates: {
          title: "6. Käytännön päivitykset",
          paragraphs: ["Saatamme päivittää tätä evästekäytäntöä ajoittain heijastaaksemme muutoksia käytännöissämme tai juridisista, operatiivisista tai lainsäädännöllisistä syistä. Sivun yläosassa oleva \"Päivitetty\"-päiväys osoittaa viimeisimmän muutoksen ajankohdan. Suosittelemme tarkistamaan käytännön säännöllisesti."]
        },
        contact: {
          title: "7. Yhteystiedot",
          paragraphs: ["Jos sinulla on kysyttävää evästeiden käytöstämme tai tästä evästekäytännöstä, otathan yhteyttä:"],
          emailLabel: "Sähköposti",
          addressLabel: "Osoite"
        }
      }
    }
  };

  const c = content[language] || content.en;
  const lu = lastUpdated[language] || lastUpdated.en;
  const s = c.sections;

  const renderCookieTable = (block, withProvider = false) => (
    <>
      <div className="bg-cream/50 p-4 rounded-lg mb-4">
        <p className="text-sm text-ink-muted mb-2"><strong>{c.statusLabel}:</strong> {block.alwaysActive ? c.alwaysActive : c.requiresConsent}</p>
        <p className="text-ink-light text-sm">{block.description}</p>
      </div>
      <table className="w-full text-sm border border-warm rounded-lg overflow-hidden mb-6">
        <thead className="bg-cream/50">
          <tr>
            <th className="text-left p-3 border-b border-warm">{c.tableCols.name}</th>
            {withProvider && <th className="text-left p-3 border-b border-warm">{c.tableCols.provider}</th>}
            <th className="text-left p-3 border-b border-warm">{c.tableCols.purpose}</th>
            <th className="text-left p-3 border-b border-warm">{c.tableCols.duration}</th>
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, idx) => (
            <tr key={idx}>
              <td className="p-3 border-b border-warm">{row.name}</td>
              {withProvider && <td className="p-3 border-b border-warm">{row.provider}</td>}
              <td className="p-3 border-b border-warm">{row.purpose}</td>
              <td className="p-3 border-b border-warm">{row.duration}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );

  return (
    <div className="min-h-screen bg-parchment" data-testid="cookie-policy-page">
      <header className="border-b border-warm py-6">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-ink-muted hover:text-ink transition-colors" data-testid="cookie-policy-back-home">
            <ArrowLeft className="w-4 h-4" />
            {c.backHome}
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="font-serif text-4xl md:text-5xl text-ink mb-4">{c.title}</h1>
        <p className="text-ink-muted mb-8">{c.lastUpdatedLabel}: {lu}</p>

        {/* Manage Cookies Banner */}
        <div className="bg-accent-teal/10 border border-accent-teal/20 rounded-lg p-6 mb-12">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-ink mb-1">{c.manageHeading}</h3>
              <p className="text-sm text-ink-muted">{c.manageBody}</p>
            </div>
            <Button
              onClick={() => cookieConsent?.showSettings()}
              className="bg-accent-teal text-white hover:bg-accent-teal/90"
              data-testid="manage-cookies-btn"
            >
              {c.manageBtn}
            </Button>
          </div>
        </div>

        <div className="prose prose-lg max-w-none">
          {/* 1. What */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-ink mb-4">{s.what.title}</h2>
            {s.what.paragraphs.map((p, i) => <p key={i} className="text-ink-light mb-4">{p}</p>)}
          </section>

          {/* 2. Types */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-ink mb-4">{s.types.title}</h2>
            <h3 className="font-semibold text-ink mb-3 mt-6">{s.types.strict.title}</h3>
            {renderCookieTable({ ...s.types.strict, alwaysActive: true })}
            <h3 className="font-semibold text-ink mb-3 mt-6">{s.types.analytics.title}</h3>
            {renderCookieTable(s.types.analytics, true)}
            <h3 className="font-semibold text-ink mb-3 mt-6">{s.types.functional.title}</h3>
            {renderCookieTable(s.types.functional)}
            <h3 className="font-semibold text-ink mb-3 mt-6">{s.types.marketing.title}</h3>
            {renderCookieTable(s.types.marketing, true)}
          </section>

          {/* 3. Consent */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-ink mb-4">{s.consent.title}</h2>
            {s.consent.paragraphs.map((p, i) => <p key={i} className="text-ink-light mb-4">{p}</p>)}
            <ul className="list-disc pl-6 text-ink-light mb-4">
              {s.consent.bullets.map((b, i) => <li key={i} dangerouslySetInnerHTML={{ __html: b }} />)}
            </ul>
            {s.consent.paragraphsAfter.map((p, i) => <p key={`pa-${i}`} className="text-ink-light mb-4">{p}</p>)}
          </section>

          {/* 4. Control */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-ink mb-4">{s.control.title}</h2>
            {s.control.paragraphs.map((p, i) => <p key={i} className="text-ink-light mb-4">{p}</p>)}
            <ul className="list-disc pl-6 text-ink-light mb-4">
              {s.control.links.map((link, i) => (
                <li key={i}>
                  <a href={link.url} className="text-accent-teal underline" target="_blank" rel="noopener noreferrer">{link.label}</a>
                </li>
              ))}
            </ul>
            {s.control.paragraphsAfter.map((p, i) => <p key={`pa-${i}`} className="text-ink-light mb-4" dangerouslySetInnerHTML={{ __html: p }} />)}
          </section>

          {/* 5. Third Party */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-ink mb-4">{s.thirdParty.title}</h2>
            {s.thirdParty.paragraphs.map((p, i) => <p key={i} className="text-ink-light mb-4">{p}</p>)}
            <ul className="list-disc pl-6 text-ink-light mb-4">
              {s.thirdParty.bullets.map((b, i) => <li key={i} dangerouslySetInnerHTML={{ __html: b }} />)}
            </ul>
          </section>

          {/* 6. Updates */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-ink mb-4">{s.updates.title}</h2>
            {s.updates.paragraphs.map((p, i) => <p key={i} className="text-ink-light mb-4">{p}</p>)}
          </section>

          {/* 7. Contact */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-ink mb-4">{s.contact.title}</h2>
            {s.contact.paragraphs.map((p, i) => <p key={i} className="text-ink-light mb-4">{p}</p>)}
            <div className="bg-cream/50 p-6 rounded-lg">
              <p className="text-ink-light mb-2"><strong>{s.contact.emailLabel}:</strong> <a href="mailto:privacy@2ds.fi" className="text-accent-teal underline">privacy@2ds.fi</a></p>
              <p className="text-ink-light"><strong>{s.contact.addressLabel}:</strong> 2DS Finland Oy, Espoo, Finland</p>
            </div>
          </section>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 p-6 bg-ink text-parchment rounded-lg">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-parchment/80">{c.bottomCta}</p>
            <Button
              onClick={() => cookieConsent?.showSettings()}
              variant="outline"
              className="border-parchment text-parchment hover:bg-parchment hover:text-ink"
            >
              {c.bottomCtaBtn}
            </Button>
          </div>
        </div>
      </main>

      <footer className="border-t border-warm py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-ink-muted text-sm">
            © {new Date().getFullYear()} 2DS Finland Oy. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CookiePolicy;
